/// <amd-module name="Wit/WorkItemForm/Header/WorkItemFormCommandBar" />
import { IFollowsService } from "Notifications/Common/Contracts/FollowsService";
import { KnownArtifactTypes } from "Notifications/Common/Services/FollowsService";
import { IMailConfigurationService } from "Notifications/Common/Services/MailConfigurationService";
import * as React from "react";
import { getTFSPageData, getTFSProject, IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { ObservableCollection, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { showMessageDialog } from "VSS/Features/PlatformUI/MessageDialog";
import { ILegacyPlatformService } from "VSS/Legacy/Legacy";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLayoutManager } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button, IButtonProps } from "VSSUI/Button";
import { copyToClipboard } from "VSSUI/Clipboard";
import { Expandable } from "VSSUI/Expandable";
import { HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { SplitButton } from "VSSUI/SplitButton";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import * as WorkItemUtils from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { getDefaultLinkableWorkItemTypeFilter, getDefaultLinkTypeFilter, newLinksFilter } from "Wit/Common/Utils/WorkItemUtils/LinkTypeFilter";
import * as CommonResources from "Wit/Common/Utils/WorkItemUtils/Resources";
import { ChangeTypeDialog } from "Wit/Common/WorkItemDialogs/Components/ChangeTypeDialog";
import { CopyWorkItemDialog } from "Wit/Common/WorkItemDialogs/Components/CopyWorkItemDialog";
import { DeleteDialog } from "Wit/Common/WorkItemDialogs/Components/DeleteDialog";
import { getTemplateMenuItem } from "Wit/Common/WorkItemDialogs/Components/EditTemplateDialog";
import { EmailWorkItemsDialog } from "Wit/Common/WorkItemDialogs/Components/EmailWorkItemsDialog";
import { MoveToProjectDialog } from "Wit/Common/WorkItemDialogs/Components/MoveToProjectDialog";
import { WorkItemFormDialog } from "Wit/Common/WorkItemDialogs/Components/WorkItemFormDialog";
import { IWorkItemLinksService } from "Wit/Common/WorkItemLinksService/WorkItemLinksService";
import { getBoardsMessagesService, getErrorMessage } from "Wit/Messages/BoardsMessagesService";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { IWorkItemFormPageContext, WorkItemFormPageContext } from "Wit/WorkItemForm/Common/Hooks";
import * as Resources from "Wit/WorkItemForm/Resources";
import { INewLinkData } from "Wit/WorkItemLinksRegistration/Contracts";
import { IWorkItem, IWorkItemBulkSaveResult, IWorkItemInfoText, IWorkItemPermissionData } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { getWorkItemEditUrl, getWorkItemHubUrl, isNewWorkItemFormRoute } from "../Common/RoutingUtils";
import { IWorkItemFormService } from "../Common/Services/IWorkItemFormService";
import { AddLinkDialog } from "../Links/Dialogs/AddLinkDialog";
import { WorkItemNotificationSettingsDialog } from "./WorkItemNotificationSettingsDialog";

const savePreferenceSetting = "WorkItemForm/SavePreference";

enum SavingPreferences {
    Save,
    SaveAndClose
}

export interface IWorkItemFormHeaderCommandBarProps {
    onDelete?: () => void;
    onDismiss?: () => void;
    onRefreshed: () => void;
    onReset: () => void;
    onSave: (callback: (result: IWorkItemBulkSaveResult[]) => void, errorCallback?: ((error: any) => void) | undefined) => void;
    currentIterationPath?: string;
}

interface IContributedItemContext {
    closeWorkItemFormDialogDelegate?: () => void;
    saveErrorCallback?: (error: string) => void;
    currentProjectName?: string;
    currentProjectGuid?: string;
    hideDelete?: boolean;
    movedToNewProject?: boolean;
    tfsContext?: any;
    workItemAvailable?: boolean;
    workItemDirty?: boolean;
    workItemTypeName?: string;
    workItemId?: number;
}

export class WorkItemFormHeaderCommandBar extends React.Component<IWorkItemFormHeaderCommandBarProps> {
    public static contextType = WorkItemFormPageContext;

    private workItem: IWorkItem;
    private expandableRef = React.createRef<Expandable>();
    private workItemPermissions = new ObservableValue<IWorkItemPermissionData | undefined>(undefined);
    private isFollowing = new ObservableValue<boolean>(false);
    private nameDataDictionary: { [refName: string]: any } = {};
    private contributedItemContext: IContributedItemContext = {};
    private contributedMenuItems = new ObservableCollection<IMenuItem>();
    private projectData: IProjectData | undefined;
    private settingService: ISettingsService;
    public context: IWorkItemFormPageContext;

    constructor(props: IWorkItemFormHeaderCommandBarProps, context: IWorkItemFormPageContext) {
        super(props);

        if (!context.pageContext || context.workItem === null) {
            throw new Error("WorkItemFormPageContext can only be used inside a WorkItemFormPageContextProvider");
        }

        this.workItem = context.workItem;
        this.projectData = getTFSProject(context.pageContext);

        this.updateNamedDataDictionary();
        this.setContributedItemContext(context);
        this.contributedMenuItems.push(
            getMenuItems(context.pageContext, "ms.vss-work-web.work-item-context-menu", this.contributedItemContext, undefined, true)
        );
        this.contributedMenuItems.push(
            getMenuItems(context.pageContext, "ms.vss-work-web.work-item-context-menu-owp", this.contributedItemContext, undefined, true)
        );
        this.contributedMenuItems.push(
            getMenuItems(context.pageContext, "ms.vss-work-web.work-item-toolbar-menu", this.contributedItemContext, undefined, true)
        );

        this.settingService = context.pageContext.getService<ISettingsService>("ISettingsService");
    }

    private setContributedItemContext = async (context: IWorkItemFormPageContext) => {
        this.contributedItemContext = {
            closeWorkItemFormDialogDelegate: this.onDeleteItem,
            saveErrorCallback: (error: string) => {
                if (context.pageContext) {
                    const messagesService = getBoardsMessagesService(context.pageContext);
                    messagesService.showMessage(getErrorMessage(error));
                }
            },
            currentProjectGuid: this.workItem.project.guid,
            currentProjectName: this.workItem.project.name,
            hideDelete: this.workItem.id < 0 || this.workItem.isDeleted(),
            movedToNewProject: this.workItem.getFieldValueByName(CoreFieldRefNames.TeamProject, true) !== this.workItem.project.name,
            workItemAvailable: this.workItem.id > 0,
            workItemId: this.workItem.id,
            workItemTypeName: this.workItem.getTypeData().name
        };

        if (context.pageContext) {
            const legacyPlatformService = context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");
            const modules = await legacyPlatformService.requireModules(["Presentation/Scripts/TFS/TFS.Host.TfsContext"]);
            const tfsContext = modules[0];
            this.contributedItemContext.tfsContext = tfsContext.TfsContext.getDefault();
        }

        this.onDirtyUpdate();
    };

    public render(): JSX.Element {
        return (
            <Observer
                contributedMenuItems={this.contributedMenuItems}
                dirty={this.workItem.dirty}
                infoText={this.workItem.infoText}
                isFollowing={this.isFollowing}
                permissions={this.workItemPermissions}
                saving={this.workItem.saving}
            >
                {(props: {
                    contributedMenuItems: IMenuItem[];
                    dirty: boolean;
                    infoText: IWorkItemInfoText;
                    isFollowing: boolean;
                    permissions: IWorkItemPermissionData | undefined;
                    saving: boolean;
                }) => {
                    const [headerCommandItems, buttonCount] = this.getCommandBarItems(
                        props.dirty,
                        props.infoText,
                        props.isFollowing,
                        props.permissions,
                        props.saving,
                        props.contributedMenuItems
                    );
                    return (
                        <HeaderCommandBar
                            className="work-item-header-command-bar flex-row flex-grow flex-self-start"
                            items={headerCommandItems}
                            buttonCount={buttonCount}
                        />
                    );
                }}
            </Observer>
        );
    }

    public componentDidMount() {
        if (this.context.pageContext) {
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<IWorkItemPermissionData>("ms.vss-work-web.work-item-permissions-data-provider").then(data => {
                if (data) {
                    this.workItemPermissions.value = data;
                }
            });
            const followsService = this.context.pageContext.getService<IFollowsService>("IFollowsService");
            followsService
                .isFollowing(KnownArtifactTypes.WorkItem, this.workItem.getUniqueId().toString())
                .then(isFollowing => (this.isFollowing.value = isFollowing));
        }

        this.workItem.dirty.subscribe(this.onDirtyUpdate);
    }

    public componentWillUnmount() {
        this.workItem.dirty.unsubscribe(this.onDirtyUpdate);
    }

    private onDirtyUpdate = () => {
        this.contributedItemContext.workItemDirty = this.workItem.dirty.value;
    };

    private getCommandBarItems(
        dirty: boolean,
        infoText: IWorkItemInfoText,
        isFollowing: boolean,
        permissions: IWorkItemPermissionData | undefined,
        saving: boolean,
        contributedMenuItems: IMenuItem[]
    ): [IHeaderCommandBarItem[], number] {
        const isDeleted = this.workItem.isDeleted();
        const isReadOnly = this.workItem.isReadOnly();
        const isNew = this.workItem.isNew();
        const { onDismiss } = this.props;

        const mailConfigurationService = this.context.pageContext!.getService<IMailConfigurationService>("IMailConfigurationService");
        const isFollowingEnabled = mailConfigurationService.isEnabled() && !this.workItem.isNew();

        const headerCommandItems: IHeaderCommandBarItem[] = [];
        let buttonCount = 0;

        // Don't return the saving promise
        const onSave = () => {
            this.props.onSave(this.onItemSaved, this.resetHistoryData);
        };

        if (!onDismiss && !isReadOnly) {
            headerCommandItems.push({
                disabled: !dirty || infoText.invalid || saving,
                groupKey: "core",
                iconProps: { iconName: "Save" },
                id: "save",
                isPrimary: true,
                rank: 10,
                text: Resources.Save,
                onActivate: onSave
            });
            buttonCount++;
        } else if (!isReadOnly) {
            const saveMenuItem = {
                disabled: !dirty || infoText.invalid || saving,
                iconProps: { iconName: "Save" },
                id: "save",
                text: Resources.Save
            };

            const saveAndCloseCommonProps = {
                disabled: !dirty || infoText.invalid || saving,
                id: "save-and-close",
                text: Resources.SaveAndClose,
                iconProps: { iconName: "Save" }
            };

            const save = () => {
                onSave();
                this.setSavePreference(SavingPreferences.Save);
            };

            const saveAndClose = () =>
                this.props.onSave(() => {
                    this.resetHistoryData();
                    onDismiss && onDismiss();
                    this.setSavePreference(SavingPreferences.SaveAndClose);
                }, this.resetHistoryData);

            headerCommandItems.push({
                id: "save-dialog",
                className: "flex-noshrink",
                groupKey: "core",
                isPrimary: true,
                rank: 10,
                subMenuProps: {
                    id: "save-submenu",
                    items: [
                        { ...saveMenuItem, onActivate: save },
                        { ...saveAndCloseCommonProps, onActivate: saveAndClose }
                    ]
                },
                text: "Save...",
                renderButton: props => {
                    let buttonProps: IButtonProps;
                    let menuPropsItem: IMenuItem;
                    const preference = this.settingService?.getEntry<{ [key: string]: SavingPreferences }>(
                        savePreferenceSetting,
                        SettingsUserScope.Me
                    )?.preference;

                    if (preference === SavingPreferences.Save) {
                        buttonProps = { ...saveMenuItem, onClick: save };
                        menuPropsItem = { ...saveAndCloseCommonProps, onActivate: saveAndClose };
                    } else {
                        buttonProps = { ...saveAndCloseCommonProps, onClick: saveAndClose };
                        menuPropsItem = { ...saveMenuItem, onActivate: save };
                    }

                    return (
                        <SplitButton
                            buttonProps={buttonProps}
                            className={props.className}
                            disabled={!dirty || infoText.invalid || saving}
                            key="save-dialog-split-button"
                            menuButtonProps={{
                                ariaLabel: Resources.MoreSaveOptions,
                                contextualMenuProps: {
                                    menuProps: {
                                        id: "save-options",
                                        items: [menuPropsItem]
                                    }
                                }
                            }}
                            primary={true}
                        />
                    );
                }
            });
            buttonCount++;
        }

        if (isFollowingEnabled) {
            const toggleFollow = async () => {
                const followsService = this.context.pageContext!.getService<IFollowsService>("IFollowsService");

                if (isFollowing) {
                    await followsService.unfollow(KnownArtifactTypes.WorkItem, this.workItem.getUniqueId().toString());
                } else {
                    await followsService.follow(KnownArtifactTypes.WorkItem, this.workItem.getUniqueId().toString());
                }

                this.isFollowing.value = !isFollowing;
            };

            const notificationSettingsDialog = (onDismiss: () => void) => (
                // Give the Dialog context for layoutManager
                <WorkItemFormPageContext.Provider value={this.context}>
                    <WorkItemNotificationSettingsDialog
                        onDismiss={(following?: boolean) => {
                            if (following !== undefined) {
                                this.isFollowing.value = following;
                            }
                            onDismiss();
                        }}
                    />
                </WorkItemFormPageContext.Provider>
            );

            headerCommandItems.push({
                id: "follow",
                className: "flex-noshrink",
                groupKey: "core",
                rank: 20,
                subMenuProps: {
                    id: "follow-menu",
                    items: [
                        {
                            id: "follow",
                            iconProps: { iconName: "View" },
                            text: isFollowing ? "Unfollow" : Resources.Follow,
                            onActivate: () => {
                                // Needs to be called like this due to wierd typing issue, () => Promise<void> can be cast as () => void fine,
                                // But not () => void | boolean
                                toggleFollow();
                            }
                        },
                        {
                            id: "follow-options",
                            iconProps: { iconName: "Settings" },
                            text: Resources.NotificationSettings,
                            onActivate: () => {
                                this.openDialog(notificationSettingsDialog);
                            }
                        }
                    ]
                },
                text: isFollowing ? Resources.Following : "Not Following",
                renderButton: props => {
                    return (
                        <SplitButton
                            buttonProps={{
                                id: "follow",
                                text: isFollowing ? Resources.Following : Resources.Follow,
                                iconProps: { iconName: "View" },
                                onClick: toggleFollow,
                                role: 'menuitem'
                            }}
                            className={props.className}
                            key="follow-split-button"
                            expandableRef={this.expandableRef}
                            menuButtonProps={{
                                ariaLabel: Resources.NotificationSettings,
                                ariaHasPopup: true,
                                contextualMenuProps: { menuProps: { id: "follow-options", items: [] } },
                                iconProps: { iconName: "Settings" },
                                role: 'menuitem'
                            }}
                            renderCallout={() => {
                                return notificationSettingsDialog(() => this.expandableRef.current && this.expandableRef.current.collapse());
                            }}
                        />
                    );
                }
            });
            buttonCount++;
        }

        if (!isDeleted) {
            headerCommandItems.push({
                ariaLabel: Resources.Refresh,
                disabled: isNew,
                groupKey: "core",
                iconProps: { iconName: "Refresh" },
                id: "refresh",
                rank: 30,
                renderButton: props => <Button {...props} key="refresh" text={undefined} />,
                subtle: true,
                text: Resources.Refresh,
                tooltipProps: { text: Resources.Refresh },
                onActivate: () => {
                    const linksService = this.context.pageContext!.getService<IWorkItemLinksService>("IWorkItemLinksService");
                    const linkTypeFilter = getDefaultLinkTypeFilter(this.workItem);
                    const maxLinkCount = Number.MAX_SAFE_INTEGER;
                    const workItemService = this.context.pageContext!.getService<IWorkItemService>("work-item-service");

                    const refreshWorkItem = async () => {
                        workItemService.cleanCache();

                        this.workItem.refresh().catch(reason => alert(reason));
                        linksService.refreshLinkData(this.workItem, { linkTypeFilter, maxLinkCount, ignoreArtifactCache: true });

                        this.props.onRefreshed();
                    };
                    if (dirty) {
                        showMessageDialog(this.context.pageContext!, {
                            message: Resources.ConfirmWorkItemRefresh,
                            onClose: result => result && refreshWorkItem()
                        });
                    } else {
                        // Old version uses an alert so using that again.
                        refreshWorkItem();
                    }
                }
            });
            buttonCount++;

            if (!isReadOnly) {
                headerCommandItems.push({
                    ariaLabel: Resources.RevertChanges,
                    disabled: isNew || !dirty,
                    groupKey: "core",
                    iconProps: { iconName: "Undo" },
                    id: "revert",
                    rank: 40,
                    renderButton: props => <Button {...props} key="revert" text={undefined} />,
                    subtle: true,
                    text: Resources.RevertChanges,
                    tooltipProps: { text: Resources.RevertChanges },
                    onActivate: () => {
                        showMessageDialog(this.context.pageContext!, {
                            message: Resources.ConfirmWorkItemRevert,
                            okButtonProps: { text: Resources.Revert },
                            onClose: result => {
                                if (result) {
                                    this.workItem.reset();
                                    this.props.onReset();
                                }
                            }
                        });
                    }
                });
                buttonCount++;

                headerCommandItems.push(
                    {
                        disabled: isNew,
                        id: "new-linked-item",
                        text: Resources.NewLinkedWorkItem,
                        iconProps: { iconName: "WorkItem" },
                        groupKey: "core",
                        rank: 100,
                        onActivate: () => {
                            this.openDialog(onDismiss => {
                                const layoutManager = this.context.pageContext!.getService<IVssLayoutManager>("IVssLayoutManager");

                                return (
                                    <AddLinkDialog
                                        originWorkItems={[this.workItem]}
                                        linkTypeFilter={newLinksFilter}
                                        linkableWorkItemTypeFilter={getDefaultLinkableWorkItemTypeFilter([this.workItem])}
                                        onNewLinksAdded={async (newLinkData: {
                                            newWorkItemData: INewLinkData;
                                            linkTypeName: string;
                                            comment: string;
                                            assignedTo?: string;
                                        }) => {
                                            const projectId = getTFSPageData(this.context.pageContext!)?.project?.id ?? this.workItem.project.guid;
                                            if (!projectId) {
                                                throw new Error(Resources.NoProjectSpecifiedMessage);
                                            }
                                            const workItemService =
                                                this.context.pageContext!.getService<IWorkItemModelService>("IWorkItemModelService");
                                            const item = await workItemService.createWorkItem(newLinkData.newWorkItemData.typeData, projectId);
                                            item.addLinks([
                                                await WorkItemLink.create(
                                                    this.context.pageContext!,
                                                    newLinkData.linkTypeName,
                                                    this.workItem.id,
                                                    newLinkData.comment
                                                )
                                            ]);
                                            item.setFieldValueByName(CoreFieldRefNames.Title, newLinkData.newWorkItemData.title);
                                            item.setFieldValueByName(CoreFieldRefNames.AssignedTo, newLinkData.assignedTo);
                                            layoutManager.renderCallout(onInnerDismiss => {
                                                const dismissFunction = () => {
                                                    onInnerDismiss();
                                                    onDismiss();
                                                };
                                                return <WorkItemFormDialog workItem={item} onDismiss={dismissFunction} />;
                                            });
                                        }}
                                        onAddLinks={() => { }}
                                        onDismiss={onDismiss}
                                        isLinkToNewItem
                                    />
                                );
                            });
                        }
                    },
                    {
                        disabled: isNew,
                        id: "change-type",
                        text: Resources.ChangeType,
                        iconProps: { iconName: "Switch" },
                        groupKey: "core",
                        rank: 110,
                        onActivate: () => {
                            this.openDialog(onDismiss => (
                                <ChangeTypeDialog
                                    onDismiss={onDismiss}
                                    selectedItems={[this.nameDataDictionary]}
                                    workItemProjectId={this.workItem.project.guid}
                                />
                            ));
                        }
                    },
                    {
                        disabled: isNew || dirty,
                        id: "move-team-project",
                        text: Resources.MoveTeamProject,
                        iconProps: { iconName: "Assign" },
                        groupKey: "core",
                        rank: 120,
                        onActivate: () => {
                            this.openDialog(onDismiss => <MoveToProjectDialog onDismiss={onDismiss} selectedItems={[this.nameDataDictionary]} />);
                        }
                    },
                    {
                        disabled: isNew,
                        id: "copy",
                        text: Resources.CopyWorkItem,
                        iconProps: { iconName: "Copy" },
                        groupKey: "core",
                        rank: 130,
                        onActivate: () => {
                            this.updateNamedDataDictionary();
                            this.openDialog(onDismiss => <CopyWorkItemDialog selectedItem={this.nameDataDictionary} onDismiss={onDismiss} />);
                        }
                    },
                    {
                        disabled: isNew || saving,
                        id: "email",
                        text: Resources.EmailWorkItem,
                        iconProps: { iconName: "Mail" },
                        groupKey: "core",
                        rank: 140,
                        onActivate: () => {
                            const referenceFieldNames = [
                                CoreFieldRefNames.Id,
                                CoreFieldRefNames.WorkItemType,
                                CoreFieldRefNames.Title,
                                CoreFieldRefNames.AssignedTo,
                                CoreFieldRefNames.State,
                                CoreFieldRefNames.Tags
                            ];

                            const columnFieldNames: string[] = referenceFieldNames
                                .map(fieldRef => this.workItem.getFieldByName(fieldRef)?.fieldDefinition.name)
                                .filter(v => v) as string[];
                            this.openDialog(onDismiss => (
                                <EmailWorkItemsDialog
                                    columnFieldNames={columnFieldNames}
                                    referenceFieldNames={referenceFieldNames}
                                    onDismiss={onDismiss}
                                    selectedItems={[this.nameDataDictionary]}
                                    workItemProjectId={this.workItem.project.guid}
                                    defaultSubject={format(Resources.EmailWorkItemDefaultSubject, this.workItem.id, this.workItem.getTitle())}
                                />
                            ));
                        }
                    }
                );

                if (!this.isCrossProjectItem()) {
                    const tfsPageService = this.context.pageContext!.getService<ITfsPageService>("ITfsPageService");
                    const teamData = tfsPageService.getHorizontalNavigationTeamData();
                    const workItemFields: { [key: string]: any } = {};
                    this.workItem.fields.forEach(field => {
                        const referenceName = field.fieldDefinition.referenceName;
                        workItemFields[referenceName] = this.workItem.getFieldValueByName(referenceName);
                    });
                    const templateMenuItem = getTemplateMenuItem(
                        this.context.pageContext!,
                        [workItemFields],
                        this.openDialog,
                        this.projectData?.id ?? "",
                        {
                            teamData: teamData,
                            currentIterationPath: this.props.currentIterationPath
                        }
                    );
                    templateMenuItem.rank = 170;
                    headerCommandItems.push(templateMenuItem);
                }

                if (isFeatureFlagEnabled(this.context.pageContext!, "WebAccess.Process.Hierarchy")) {
                    headerCommandItems.push({
                        id: "customize",
                        text: Resources.Customize,
                        iconProps: { iconName: "Edit" },
                        groupKey: "customize-group",
                        rank: 300,
                        onActivate: () => {
                            let url: string;
                            if (this.workItem.project.process!.isInherited) {
                                url = routeUrl(this.context.pageContext!, "ms.vss-work-web.work-customization-route", {
                                    action: "layout",
                                    "process-id": this.workItem.project.process!.id,
                                    "type-id": this.workItem.workItemType.referenceName
                                });
                            } else {
                                url = routeUrl(this.context.pageContext!, "ms.vss-work-web.work-customization-route", {
                                    "launch-wizard": "true",
                                    "wizard-project-id": this.projectData?.id!,
                                    "wizard-process-id": this.workItem.project.process?.id ?? "",
                                    "wizard-wit-ref-name": this.workItem.workItemType.referenceName,
                                    "wizard-project-name": this.workItem.project.name
                                });
                            }
                            window.open(url);
                        }
                    });
                }

                if (!isNew) {
                    headerCommandItems.push({
                        id: "new-branch",
                        text: Resources.NewBranch,
                        iconProps: { iconName: "OpenSource" },
                        groupKey: "repos",
                        onActivate: () => {
                            const contributionService = this.context.pageContext!.getService<IVssContributionService>("IVssContributionService");
                            contributionService.executeCommandEx(
                                "ms.vss-code-web.tags-version-dialog-actions-command",
                                {
                                    dependencies: [
                                        "ms.vss-code-web.version-dialogs-content",
                                        "ms.vss-code-web.versioncontrol-viewmodel-data-provider"
                                    ],
                                    serviceName: "IVersionDialogActionsService",
                                    methodName: "createBranch"
                                },
                                {
                                    initialWorkItemIds: [this.workItem.id],
                                    allowChangingRepository: true
                                }
                            );
                        },
                        rank: 200
                    });
                    if (
                        permissions?.workItemDeletePermission?.hasPermission &&
                        !WorkItemUtils.TestItemTypes.includes(this.workItem.getFieldValueByName(CoreFieldRefNames.WorkItemType))
                    ) {
                        headerCommandItems.push({
                            disabled: isNew,
                            id: "delete",
                            text: Resources.Delete,
                            iconProps: { iconName: "Delete" },
                            groupKey: "core",
                            rank: 160,
                            onActivate: () => {
                                this.openDialog(onDismiss => (
                                    <DeleteDialog onDismiss={onDismiss} selectedItems={[this.nameDataDictionary]} onDeleteItems={this.onDeleteItem} />
                                ));
                            }
                        });
                    }
                    headerCommandItems.push({
                        disabled: isNew,
                        id: "copy-link",
                        text: CommonResources.CopyLink,
                        iconProps: { iconName: "Link" },
                        groupKey: "core",
                        rank: 170,
                        onActivate: this.copyLink
                    });
                }
            }
        }

        if (isDeleted && !isNew && !isReadOnly && permissions) {
            if (permissions.workItemDestroyPermission?.hasPermission) {
                headerCommandItems.push({ id: "destroy", iconProps: { iconName: "Delete" }, subtle: true, important: true, rank: 50 });
                buttonCount++;
            }

            if (permissions.workItemDeletePermission?.hasPermission) {
                headerCommandItems.push({ id: "restore", iconProps: { iconName: "Undo" }, subtle: true, important: true, rank: 60 });
                buttonCount++;
            }
        }

        // Filter out contributed items with duplicate ids
        const uniqueContributedMenuItems = contributedMenuItems.filter((item, pos, self) => {
            return self.findIndex(selfItem => selfItem.id == item.id) == pos;
        });

        const testWorkItemDelete = uniqueContributedMenuItems.find(item => item.id === "test-work-item-delete");
        if (testWorkItemDelete) {
            testWorkItemDelete.iconProps = { iconName: "ChromeClose" };
        }

        headerCommandItems.push(...uniqueContributedMenuItems);

        return [headerCommandItems, buttonCount];
    }

    private copyLink = (): void => {
        if (this.context.pageContext) {
            const project = this.workItem.project.name;
            const url = WorkItemUtils.getWorkItemUrl(this.context.pageContext, this.workItem.id, project);
            const workItemUrl = window.location.origin + url;
            copyToClipboard(workItemUrl);
        }
    };

    private isCrossProjectItem() {
        return this.workItem.project.name !== this.projectData?.name;
    }

    private onItemSaved = () => {
        this.resetHistoryData();
        this.updateNamedDataDictionary();
        if (isNewWorkItemFormRoute(this.context.pageContext!) && !this.props.onDismiss) {
            this.context
                .pageContext!.getService<IVssHistoryService>("IVssHistoryService")
                .replaceState(undefined, getWorkItemEditUrl(this.context.pageContext!, this.workItem.getUniqueId().toString()));
        }
    };

    private onDeleteItem = () => {
        this.props.onDelete?.();
        if (this.props.onDismiss) {
            this.props.onDismiss();
        } else if (isNewWorkItemFormRoute(this.context.pageContext!)) {
            FastPageSwitch(this.context.pageContext!, getWorkItemHubUrl(this.context.pageContext!), false);
        }
    };

    private updateNamedDataDictionary = () => {
        const fieldLookup = this.workItem.getFieldLookup();

        const fieldData = this.workItem.getFieldData();

        for (const id in fieldData) {
            if (fieldLookup[id]) {
                this.nameDataDictionary[fieldLookup[id]!.referenceName] = fieldData[id];
            }
        }
    };

    private resetHistoryData = () => {
        this.context.pageContext?.getService<IWorkItemFormService>("IWorkItemFormService").resetHistoryData();
    };

    private openDialog = (callout: (onDismiss: () => void) => JSX.Element): void => {
        const layoutManager = this.context.pageContext!.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => callout(onDismiss));
    };

    private setSavePreference = (preference: SavingPreferences) => {
        const currentPreference = this.settingService?.getEntry<{ [key: string]: SavingPreferences }>(
            savePreferenceSetting,
            SettingsUserScope.Me
        )?.preference;
        if (currentPreference !== preference) {
            this.settingService?.setEntries({ [`${savePreferenceSetting}/preference`]: preference }, SettingsUserScope.Me);
        }
    };
}
